import { useAuth } from '../../auth/useAuth';
import RegisterTile from './RegisterTile';
import type { RegisterKey } from '../../types/qms';
import type { RegisterMetrics } from '../../hooks/useRegisterMetrics';
import styles from './TileGrid.module.css';

/**
 * TileGrid (04-05) — lays out the five register tiles for the signed-in role.
 *
 * Oversight sees all five with equal weight. Finance / HRWHS see the SAME five
 * tiles (their scope token already narrows each tile's NUMBERS — 04-03), but the
 * registers most relevant to their role are emphasised first so the surface
 * leads with what matters to them (a presentation cue, not an access gate —
 * 04-CONTEXT.md §3). CareManager sees all five, Portfolio-scoped.
 *
 * The five-register vocabulary is fixed; only ORDER + emphasis vary by role.
 */

/** Canonical register order (the oversight default). */
const ALL_REGISTERS: RegisterKey[] = [
  'incidents',
  'complaints',
  'ci',
  'feedback',
  'risk',
];

/**
 * Registers a role leads with (emphasised first). Finance & HRWHS approximate
 * via category subsets that live on Incidents + Complaints (04-04 §4), so those
 * tiles lead for them; Risk follows (financial / WHS risk categories). All five
 * still render — narrowing is the scope token's job, not hiding tiles.
 */
const EMPHASIS: Partial<Record<string, RegisterKey[]>> = {
  Finance: ['incidents', 'complaints', 'risk'],
  HRWHS: ['incidents', 'complaints', 'risk'],
};

/** Order the registers for a role: emphasised first, then the rest in order. */
function orderFor(emphasised: RegisterKey[] | undefined): RegisterKey[] {
  if (!emphasised || emphasised.length === 0) return ALL_REGISTERS;
  const lead = emphasised.filter((k) => ALL_REGISTERS.includes(k));
  const rest = ALL_REGISTERS.filter((k) => !lead.includes(k));
  return [...lead, ...rest];
}

interface TileGridProps {
  metrics: Record<RegisterKey, RegisterMetrics>;
}

export default function TileGrid({ metrics }: TileGridProps) {
  const { roles } = useAuth();

  // A single emphasis set if the login carries exactly one emphasising role;
  // Oversight / CareManager (and multi-role logins) get the canonical order.
  const emphasisRole = roles.find((r) => EMPHASIS[r] !== undefined);
  const order = roles.includes('Oversight')
    ? ALL_REGISTERS
    : orderFor(emphasisRole ? EMPHASIS[emphasisRole] : undefined);

  const emphasised = new Set(
    emphasisRole && !roles.includes('Oversight') ? EMPHASIS[emphasisRole] : [],
  );

  return (
    <div className={styles.grid} role="list">
      {order.map((key) => (
        <div
          key={key}
          role="listitem"
          className={emphasised.has(key) ? styles.emphasised : undefined}
        >
          <RegisterTile registerKey={key} metrics={metrics[key]} />
        </div>
      ))}
    </div>
  );
}
